import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, DollarSign, Mail, BookOpen, MapPin } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { AudioUploadTab, CsvImportTab, SevaClubMembersTab } from "./admin/AdminTabs";
import { BroadcastTab, AIPostsTab } from "./admin/MarketingTabs";
import { LiveAartiTab, CronRunsTab } from "./admin/SettingsTabs";
import { TeachersAdminTab } from "./admin/TeachersAdminTab";
import { RevenueTab, SuppliersTab } from "./admin/RevenueSuppliersTab";
import { AdsAdminTab, VerificationsAdminTab, RedemptionsAdminTab, ExportAdminTab } from "./admin/MarketplaceAdminTabs";
import { FestivalsAdminTab, DeitiesAdminTab } from "./admin/ContentAdminTabs";
import { NgosAdminTab, NgoProjectsAdminTab, ScholarshipsAdminTab, SevaFundAdminTab } from "./admin/NgoAdminTabs";
import ImpactReportsAdminTab from "./admin/ImpactReportsAdminTab";

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tx, setTx] = useState([]);
  const [users, setUsers] = useState([]);
  const [verseForm, setVerseForm] = useState({ sanskrit: "", transliteration: "", translation: "", reference: "", date: "" });

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/stats").then((r) => setStats(r.data));
      api.get("/newsletter").then((r) => setSubs(r.data.items));
      api.get("/travel/enquiries").then((r) => setEnquiries(r.data.items));
      api.get("/admin/transactions").then((r) => setTx(r.data.items));
      api.get("/admin/users").then((r) => setUsers(r.data.items));
    }
  }, [user]);

  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const submitVerse = async (e) => {
    e.preventDefault();
    try {
      await api.post("/verses", verseForm);
      toast.success("Verse published");
      setVerseForm({ sanskrit: "", transliteration: "", translation: "", reference: "", date: "" });
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">Admin Dashboard</div>
      <h1 className="font-serif text-5xl text-[#4A0E17] mt-2">Temple Operations</h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-5 mt-10">
          {[
            { icon: Users, label: "Devotees", value: stats.users },
            { icon: Mail, label: "Subscribers", value: stats.newsletter_subscribers },
            { icon: MapPin, label: "Packages", value: stats.packages },
            { icon: BookOpen, label: "Enquiries", value: stats.enquiries },
            { icon: DollarSign, label: "Paid Revenue", value: `$${(stats.donations.total_usd + stats.bookings.total_usd).toFixed(0)}` },
          ].map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`} className="bg-[#FFFDF9] sacred-border rounded-2xl p-5">
              <s.icon className="w-5 h-5 text-[#E65100]" />
              <div className="font-serif text-3xl text-[#4A0E17] mt-3">{s.value}</div>
              <div className="overline">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="transactions" className="mt-14">
        <TabsList className="bg-[#FFFDF9] border border-[#D4AF37]/40 flex flex-wrap h-auto">
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Devotees</TabsTrigger>
          <TabsTrigger value="club" data-testid="tab-seva-club">Seva Club</TabsTrigger>
          <TabsTrigger value="audio" data-testid="tab-audio">Audio</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import">CSV Import</TabsTrigger>
          <TabsTrigger value="verse" data-testid="tab-verse">Publish Verse</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="tab-broadcast">Broadcast</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">AI Posts</TabsTrigger>
          <TabsTrigger value="aarti" data-testid="tab-aarti">Live Aarti</TabsTrigger>
          <TabsTrigger value="cron" data-testid="tab-cron">Cron Runs</TabsTrigger>
          <TabsTrigger value="teachers" data-testid="tab-teachers">Teachers</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="ads" data-testid="tab-ads">Ads</TabsTrigger>
          <TabsTrigger value="verifications" data-testid="tab-verifications">Verifications</TabsTrigger>
          <TabsTrigger value="redemptions" data-testid="tab-redemptions">Wallet Payouts</TabsTrigger>
          <TabsTrigger value="festivals" data-testid="tab-festivals">Festivals</TabsTrigger>
          <TabsTrigger value="deities" data-testid="tab-deities">Deities</TabsTrigger>
          <TabsTrigger value="export" data-testid="tab-export">CSV Export</TabsTrigger>
          <TabsTrigger value="ngos" data-testid="tab-ngos">NGOs</TabsTrigger>
          <TabsTrigger value="ngo-projects" data-testid="tab-ngo-projects">NGO Projects</TabsTrigger>
          <TabsTrigger value="scholarships" data-testid="tab-scholarships">Scholarships</TabsTrigger>
          <TabsTrigger value="seva-fund" data-testid="tab-seva-fund">Seva Fund & Tiers</TabsTrigger>
          <TabsTrigger value="impact" data-testid="tab-impact">Impact Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="mt-6">
          <div className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Donor/Traveler</th>
                <th className="text-left px-4 py-3">Date</th>
              </tr></thead>
              <tbody>
                {tx.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#63534E]">No transactions yet</td></tr>}
                {tx.map((t) => (
                  <tr key={t.id} className="border-t border-[#D4AF37]/20">
                    <td className="px-4 py-3 capitalize">{t.type}</td>
                    <td className="px-4 py-3 font-mono">${t.amount?.toFixed(2)}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${t.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{t.payment_status}</span></td>
                    <td className="px-4 py-3">{t.donor_name || t.traveler_name || "—"} <br/><span className="text-xs text-[#63534E]">{t.donor_email || t.traveler_email}</span></td>
                    <td className="px-4 py-3 font-mono text-xs">{new Date(t.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6">
          <div className="space-y-4">
            {enquiries.length === 0 && <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-10 text-center text-[#63534E]">No enquiries yet</div>}
            {enquiries.map((e) => (
              <div key={e.id} className="bg-[#FFFDF9] sacred-border rounded-2xl p-5">
                <div className="flex items-center justify-between"><div className="font-semibold text-[#4A0E17]">{e.name}</div><div className="text-xs text-[#63534E] font-mono">{new Date(e.created_at).toLocaleString()}</div></div>
                <div className="text-sm text-[#63534E] mt-1">{e.email} · {e.phone || "no phone"} · {e.travelers} travelers · {e.preferred_month || "flexible month"}</div>
                {e.message && <p className="text-[#1F1412] mt-3 text-sm">{e.message}</p>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="subscribers" className="mt-6">
          <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
            <div className="text-sm text-[#63534E] mb-4">{subs.length} subscribers</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto">
              {subs.map((s) => (
                <div key={s.id} className="text-sm text-[#1F1412] border border-[#D4AF37]/20 rounded-lg p-3">
                  {s.name && <div className="font-semibold">{s.name}</div>}
                  <div className="font-mono text-xs text-[#63534E]">{s.email}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Joined</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-[#D4AF37]/20">
                    <td className="px-4 py-3">{u.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3 capitalize">{u.role}</td>
                    <td className="px-4 py-3 font-mono text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="verse" className="mt-6">
          <form onSubmit={submitVerse} data-testid="publish-verse-form" className="bg-[#FFFDF9] sacred-border rounded-2xl p-7 max-w-3xl space-y-4">
            <Input data-testid="verse-date" type="date" placeholder="Date (YYYY-MM-DD) — leave blank for today" value={verseForm.date} onChange={(e) => setVerseForm({ ...verseForm, date: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
            <Textarea data-testid="verse-sanskrit" required placeholder="Sanskrit verse (Devanagari)" rows={3} value={verseForm.sanskrit} onChange={(e) => setVerseForm({ ...verseForm, sanskrit: e.target.value })} className="bg-white border-[#D4AF37]/40 font-devanagari text-lg" />
            <Textarea data-testid="verse-transliteration" placeholder="Transliteration (optional)" rows={2} value={verseForm.transliteration} onChange={(e) => setVerseForm({ ...verseForm, transliteration: e.target.value })} className="bg-white border-[#D4AF37]/40" />
            <Textarea data-testid="verse-translation" required placeholder="English translation / meaning" rows={3} value={verseForm.translation} onChange={(e) => setVerseForm({ ...verseForm, translation: e.target.value })} className="bg-white border-[#D4AF37]/40" />
            <Input data-testid="verse-reference" placeholder="Reference (e.g., Shiva Mahimna Stotra v.3)" value={verseForm.reference} onChange={(e) => setVerseForm({ ...verseForm, reference: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
            <Button data-testid="verse-publish-submit" type="submit" className="bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-11 px-8">Publish verse</Button>
          </form>
        </TabsContent>

        <TabsContent value="club" className="mt-6"><SevaClubMembersTab /></TabsContent>
        <TabsContent value="audio" className="mt-6"><AudioUploadTab /></TabsContent>
        <TabsContent value="import" className="mt-6"><CsvImportTab /></TabsContent>
        <TabsContent value="broadcast" className="mt-6"><BroadcastTab /></TabsContent>
        <TabsContent value="ai" className="mt-6"><AIPostsTab /></TabsContent>
        <TabsContent value="aarti" className="mt-6"><LiveAartiTab /></TabsContent>
        <TabsContent value="cron" className="mt-6"><CronRunsTab /></TabsContent>
        <TabsContent value="teachers" className="mt-6"><TeachersAdminTab /></TabsContent>
        <TabsContent value="revenue" className="mt-6"><RevenueTab /></TabsContent>
        <TabsContent value="suppliers" className="mt-6"><SuppliersTab /></TabsContent>
        <TabsContent value="ads" className="mt-6"><AdsAdminTab /></TabsContent>
        <TabsContent value="verifications" className="mt-6"><VerificationsAdminTab /></TabsContent>
        <TabsContent value="redemptions" className="mt-6"><RedemptionsAdminTab /></TabsContent>
        <TabsContent value="festivals" className="mt-6"><FestivalsAdminTab /></TabsContent>
        <TabsContent value="deities" className="mt-6"><DeitiesAdminTab /></TabsContent>
        <TabsContent value="export" className="mt-6"><ExportAdminTab /></TabsContent>
        <TabsContent value="ngos" className="mt-6"><NgosAdminTab /></TabsContent>
        <TabsContent value="ngo-projects" className="mt-6"><NgoProjectsAdminTab /></TabsContent>
        <TabsContent value="scholarships" className="mt-6"><ScholarshipsAdminTab /></TabsContent>
        <TabsContent value="seva-fund" className="mt-6"><SevaFundAdminTab /></TabsContent>
        <TabsContent value="impact" className="mt-6"><ImpactReportsAdminTab /></TabsContent>
      </Tabs>
    </div>
  );
}
